import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";
import { Toaster } from "@/components/ui/sonner";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import ogImage from "@/assets/og-mao-de-obra-temporaria-para-construcao-civil-selectus-rh.jpg.asset.json";
import logoAsset from "@/assets/logo-selectus-rh.webp.asset.json";

const OG_IMAGE_URL = `https://lp.selectusrh.com${ogImage.url}`;
const GOOGLE_FONTS_HREF =
  "https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=Inter:wght@400;500;600&display=swap";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Mão de Obra Temporária para Construção Civil | Selectus RH" },
      { name: "description", content: "Mão de obra temporária para construção civil em todo o Brasil. Mais de 20 anos de experiência. Peça seu orçamento agora!" },
      { name: "author", content: "Selectus RH" },
      { name: "keywords", content: "mão de obra temporária para construção civil, terceirização construção civil, mão de obra construção civil, Selectus RH" },
      { name: "robots", content: "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" },
      { name: "googlebot", content: "index, follow" },
      { name: "google-site-verification", content: "pB4tCKASIOlMnH54eX77d3oH9ZiLx_jNh9a50-lyIEE" },
      { name: "geo.region", content: "BR-SP" },
      { name: "geo.placename", content: "São Paulo" },
      { property: "og:title", content: "Mão de Obra Temporária para Construção Civil | Selectus RH" },
      { property: "og:description", content: "Mão de obra temporária para construção civil em todo o Brasil. Mais de 20 anos de experiência. Peça seu orçamento agora!" },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Selectus RH" },
      { property: "og:locale", content: "pt_BR" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:site", content: "@SelectusRH" },
      { name: "twitter:title", content: "Mão de Obra Temporária para Construção Civil | Selectus RH" },
      { name: "twitter:description", content: "Mão de obra temporária para construção civil em todo o Brasil. Mais de 20 anos de experiência. Peça seu orçamento agora!" },
      { property: "og:image", content: OG_IMAGE_URL },
      { name: "twitter:image", content: OG_IMAGE_URL },
    ],
    links: [
      {
        rel: "stylesheet",
        href: appCss,
      },
      { rel: "icon", href: "/favicon.png", type: "image/png" },
      { rel: "shortcut icon", href: "/favicon.png", type: "image/png" },
      { rel: "apple-touch-icon", href: "/favicon.png" },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      { rel: "alternate", hrefLang: "pt-BR", href: "https://lp.selectusrh.com/" },
      { rel: "preload", as: "image", href: logoAsset.url, type: "image/webp", fetchpriority: "high" } as any,
    ],
    scripts: [
      {
        children: `(function(){var l=document.createElement('link');l.rel='stylesheet';l.href=${JSON.stringify(GOOGLE_FONTS_HREF)};l.media='print';l.onload=function(){this.media='all';};document.head.appendChild(l);})();`,
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": ["Organization", "LocalBusiness", "EmploymentAgency"],
              "@id": "https://lp.selectusrh.com/#organization",
              name: "Selectus RH",
              legalName: "Selectus RH",
              alternateName: "Selectus Recursos Humanos",
              url: "https://lp.selectusrh.com/",
              description: "Empresa especializada em mão de obra temporária para construção civil, com mais de 20 anos de experiência em recrutamento, seleção e gestão trabalhista.",
              slogan: "Mão de obra temporária para construção civil com mais de 20 anos de experiência",
              foundingDate: "2003",
              priceRange: "$$",
              taxID: "62.160.724/0001-50",
              vatID: "62.160.724/0001-50",
              telephone: "+55-11-98098-0023",
              email: "contato@selectusrh.com",
              image: "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/926a37d5-812e-4634-a8c8-957153b30174/id-preview-bf6f8e90--b2536642-43ff-4492-9ed0-931be9a3fd0c.lovable.app-1783310824345.png",
              logo: "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/926a37d5-812e-4634-a8c8-957153b30174/id-preview-bf6f8e90--b2536642-43ff-4492-9ed0-931be9a3fd0c.lovable.app-1783310824345.png",
              address: {
                "@type": "PostalAddress",
                streetAddress: "Av. Paulista, 1471 - Conj. 511",
                addressLocality: "São Paulo",
                addressRegion: "SP",
                postalCode: "01311-200",
                addressCountry: "BR",
              },
              geo: {
                "@type": "GeoCoordinates",
                latitude: -23.5613,
                longitude: -46.6558,
              },
              contactPoint: {
                "@type": "ContactPoint",
                telephone: "+55-11-98098-0023",
                contactType: "sales",
                areaServed: "BR",
                availableLanguage: ["Portuguese"],
              },
              sameAs: [
                "https://www.instagram.com/rhselectus/",
                "https://www.facebook.com/rhselectus/",
                "https://www.linkedin.com/company/rhselectus",
                "https://www.linkedin.com/in/adriana-dutra-50246730/",
                "https://share.google/dopXFQlzjcpPZ1UNz",
              ],
              openingHoursSpecification: {
                "@type": "OpeningHoursSpecification",
                dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
                opens: "08:00",
                closes: "18:00",
              },
              areaServed: [
                { "@type": "State", name: "São Paulo" },
                { "@type": "City", name: "São Paulo" },
                { "@type": "City", name: "Campinas" },
                { "@type": "City", name: "Jundiaí" },
                { "@type": "City", name: "Sorocaba" },
                { "@type": "City", name: "Ribeirão Preto" },
                { "@type": "City", name: "Santo André" },
                { "@type": "City", name: "São Bernardo do Campo" },
                { "@type": "City", name: "Barueri" },
                { "@type": "City", name: "Guarulhos" },
              ],
              knowsAbout: [
                "Mão de obra temporária para construção civil",
                "Terceirização de mão de obra",
                "Recrutamento e seleção",
                "Gestão trabalhista",
                "Contratação CLT temporária",
                "Alocação de profissionais em canteiros de obra",
                "Lei 6.019/74 (trabalho temporário)",
                "NR-18 (segurança na construção)",
                "Encargos trabalhistas",
                "Folha de pagamento de obra",
              ],
              knowsLanguage: "pt-BR",
              makesOffer: {
                "@type": "Offer",
                itemOffered: { "@id": "https://lp.selectusrh.com/#service" },
              },
            },
            {
              "@type": "Service",
              "@id": "https://lp.selectusrh.com/#service",
              serviceType: "Mão de obra temporária para construção civil",
              provider: { "@id": "https://lp.selectusrh.com/#organization" },
              areaServed: "Brasil",
              description: "Recrutamento, contratação e gestão de mão de obra temporária para construção civil: pedreiros, serventes, armadores, carpinteiros, encanadores, eletricistas, encarregados e mestres de obra. Mobilização em até 3 dias úteis.",
              audience: { "@type": "BusinessAudience", audienceType: "Construtoras, incorporadoras e empreiteiras" },
              category: "Terceirização de mão de obra na construção civil",
              hasOfferCatalog: {
                "@type": "OfferCatalog",
                name: "Funções fornecidas",
                itemListElement: [
                  { "@type": "Offer", itemOffered: { "@type": "Service", name: "Pedreiro" } },
                  { "@type": "Offer", itemOffered: { "@type": "Service", name: "Servente de obra" } },
                  { "@type": "Offer", itemOffered: { "@type": "Service", name: "Meio-oficial" } },
                  { "@type": "Offer", itemOffered: { "@type": "Service", name: "Armador" } },
                  { "@type": "Offer", itemOffered: { "@type": "Service", name: "Carpinteiro" } },
                  { "@type": "Offer", itemOffered: { "@type": "Service", name: "Encanador" } },
                  { "@type": "Offer", itemOffered: { "@type": "Service", name: "Eletricista predial" } },
                  { "@type": "Offer", itemOffered: { "@type": "Service", name: "Pintor" } },
                  { "@type": "Offer", itemOffered: { "@type": "Service", name: "Encarregado de obra" } },
                  { "@type": "Offer", itemOffered: { "@type": "Service", name: "Mestre de obras" } },
                ],
              },
            },
            {
              "@type": "WebSite",
              "@id": "https://lp.selectusrh.com/#website",
              url: "https://lp.selectusrh.com/",
              name: "Selectus RH",
              inLanguage: "pt-BR",
              publisher: { "@id": "https://lp.selectusrh.com/#organization" },
            },
            {
              "@type": "FAQPage",
              "@id": "https://lp.selectusrh.com/#faq",
              inLanguage: "pt-BR",
              speakable: {
                "@type": "SpeakableSpecification",
                cssSelector: ["h1", "h2", "[data-speakable]"],
              },
              mainEntity: [
                { "@type": "Question", name: "Quais cidades a Selectus RH atende?", acceptedAnswer: { "@type": "Answer", text: "Atendemos toda a Grande São Paulo, Campinas, Jundiaí, Sorocaba, ABC Paulista, Alphaville e Ribeirão Preto, com mobilização rápida para canteiros em qualquer região." } },
                { "@type": "Question", name: "Como funciona a contratação de mão de obra temporária?", acceptedAnswer: { "@type": "Answer", text: "Fazemos seleção, contratação, gestão trabalhista e alocação dos profissionais no seu canteiro. Você recebe uma fatura única e nós cuidamos de encargos, folha e substituições." } },
                { "@type": "Question", name: "A terceirização reduz riscos trabalhistas?", acceptedAnswer: { "@type": "Answer", text: "Sim. Assumimos vínculos, encargos e obrigações trabalhistas dos profissionais alocados, reduzindo drasticamente o passivo da sua construtora." } },
                { "@type": "Question", name: "Em quanto tempo é possível disponibilizar profissionais?", acceptedAnswer: { "@type": "Answer", text: "Em até 3 dias úteis para demandas padrão. Para urgências, mobilizamos equipes em 24 a 48 horas." } },
                { "@type": "Question", name: "Quais funções vocês fornecem?", acceptedAnswer: { "@type": "Answer", text: "Pedreiros, serventes, meio-oficiais, armadores, carpinteiros, encanadores, eletricistas, pintores, encarregados e mestres de obra." } },
                { "@type": "Question", name: "O que é mão de obra temporária na construção civil?", acceptedAnswer: { "@type": "Answer", text: "É a contratação de profissionais por meio de uma empresa especializada, com regime CLT temporário previsto na Lei 6.019/74. A construtora recebe os profissionais no canteiro sem assumir o vínculo empregatício direto, e a empresa de RH cuida de folha, encargos, exames e substituições." } },
                { "@type": "Question", name: "Qual a diferença entre trabalho temporário e terceirização?", acceptedAnswer: { "@type": "Answer", text: "O trabalho temporário (Lei 6.019/74) atende demandas transitórias por até 180 dias, prorrogáveis por mais 90. A terceirização (Lei 13.429/17) permite alocar profissionais por prazo indeterminado em atividades meio ou fim. Nos dois casos o vínculo é com a empresa prestadora, não com a construtora." } },
                { "@type": "Question", name: "Quanto custa terceirizar mão de obra para construção civil?", acceptedAnswer: { "@type": "Answer", text: "O custo depende da função, quantidade de profissionais, local da obra e prazo. Em média, o valor gira em torno do salário-base da categoria mais encargos e taxa de administração. Peça um orçamento em lp.selectusrh.com para valores exatos por função." } },
                { "@type": "Question", name: "É legal terceirizar pedreiros e serventes na obra?", acceptedAnswer: { "@type": "Answer", text: "Sim. Desde a Reforma Trabalhista (Lei 13.467/17) e a Lei 13.429/17, é permitido terceirizar qualquer atividade, inclusive atividade-fim como pedreiros e serventes em canteiros de obra, desde que a prestadora seja empresa idônea e cumpra as obrigações trabalhistas." } },
                { "@type": "Question", name: "A Selectus RH assume responsabilidade trabalhista pelos profissionais?", acceptedAnswer: { "@type": "Answer", text: "Sim. Assumimos integralmente o vínculo CLT, folha de pagamento, INSS, FGTS, férias, 13º, exames admissionais e demissionais, EPIs conforme NR-18 e afastamentos. A construtora tem responsabilidade subsidiária apenas em caso de inadimplência comprovada." } },
                { "@type": "Question", name: "Vocês fornecem EPI e cumprem NR-18?", acceptedAnswer: { "@type": "Answer", text: "Sim. Todos os profissionais recebem EPIs adequados à função, treinamento básico de segurança e exames ocupacionais em dia. Cumprimos integralmente a NR-18 e demais normas regulamentadoras da construção civil." } },
                { "@type": "Question", name: "Se um profissional faltar ou não render, vocês substituem?", acceptedAnswer: { "@type": "Answer", text: "Sim. Fazemos substituição em até 48 horas em caso de falta, desligamento ou baixo desempenho, sem custo adicional para a construtora. Mantemos banco de talentos ativo justamente para garantir continuidade da obra." } },
              ],
            },
            {
              "@type": "BreadcrumbList",
              "@id": "https://lp.selectusrh.com/#breadcrumb",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Início", item: "https://lp.selectusrh.com/" },
              ],
            },
          ],
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="pt-BR">
      <head>
        <HeadContent />
        <noscript>
          <link rel="stylesheet" href={GOOGLE_FONTS_HREF} />
        </noscript>
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  return (
    <QueryClientProvider client={queryClient}>
      {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
      <Outlet />
      <Toaster richColors position="top-right" />
    </QueryClientProvider>
  );
}
